import { useRequiredContext } from "@redotech/react-util/context";
import { toast } from "@redotech/redo-web/alert";
import { useMutation } from "@tanstack/react-query";
import { ChangeEvent, useCallback, useState } from "react";
import { RedoAdminClientContext } from "../../../../client/context";
import { uploadZendeskConversations } from "../../../../client/team";

const CHUNK_SIZE = 100;

interface UseZendeskConversationsImportOptions {
  teamId: string | undefined;
}

export function useZendeskConversationsImport({
  teamId,
}: UseZendeskConversationsImportOptions) {
  const client = useRequiredContext(RedoAdminClientContext);
  const [file, setFile] = useState<File | null>(null);
  const [merchantEmail, setMerchantEmail] = useState("");
  const [progress, setProgress] = useState(0);

  const mutation = useMutation({
    mutationFn: async (fileToUpload: File) => {
      if (!teamId) {
        throw new Error("Team ID is required");
      }

      const text = await fileToUpload.text();
      const lines = text.split("\n").filter((line) => line.trim());

      const chunks: string[][] = [];
      for (let i = 0; i < lines.length; i += CHUNK_SIZE) {
        chunks.push(lines.slice(i, i + CHUNK_SIZE));
      }

      let importedCount = 0;
      let skippedCount = 0;

      setProgress(0);
      for (let i = 0; i < chunks.length; i++) {
        const chunkFile = new File([chunks[i]!.join("\n")], fileToUpload.name, {
          type: "application/x-ndjson",
        });
        const result = await uploadZendeskConversations(client, {
          teamId,
          file: chunkFile,
          merchantEmail: merchantEmail || undefined,
        });
        importedCount += result.importedCount;
        skippedCount += result.skippedCount;
        setProgress(Math.round(((i + 1) / chunks.length) * 100));
      }

      return { importedCount, skippedCount };
    },
    onSuccess: (result) => {
      toast(
        `Zendesk conversations imported: ${result.importedCount} imported, ${result.skippedCount} skipped`,
        { variant: "success" },
      );
      setFile(null);
      setProgress(0);
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast(`Failed to import Zendesk conversations: ${errorMessage}`, {
        variant: "error",
      });
    },
  });

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files[0]) {
        setFile(files[0]);
      }
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    if (file && teamId) {
      mutation.mutate(file);
    }
  }, [file, teamId, mutation]);

  return {
    file,
    loading: mutation.isPending,
    progress,
    merchantEmail,
    setMerchantEmail,
    handleFileChange,
    handleSubmit,
  };
}
